import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CdkDrag, CdkDragEnd, Point } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FuseConfigService } from '@fuse/services/config';
import { AuthService } from 'app/core/auth/auth.service';
import { HelpDeskComponent } from 'app/modules/modals/help-desk/help-desk.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'settings',
    templateUrl: './settings.component.html',
    styles: [
        `
            settings {
                position: static;
                display: block;
                flex: none;
                width: auto;
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [MatIconModule, MatButtonModule, MatTooltipModule, CdkDrag],
})
export class SettingsComponent implements OnInit, OnDestroy {
    config: any;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    isAuthenticated: boolean = false;
    fabDragPosition = { x: 0, y: 0 };
    private _wasDragged = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        public dialog: MatDialog,
        private _authService: AuthService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------
    /**
     * On init
     *
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this.isAuthenticated = this.isAuthenticatedUser();

        this._fuseConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
            // Store the config
            this.config = config;
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        if (!this._authService.accessToken) {
            return;
        }
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the layout on the config
     *
     * @param layout
     */
    setLayout(layout: string): void {
        // Clear the 'layout' query param to allow layout changes
        if (!this._authService.accessToken) {
            return;
        }
        this._router
            .navigate([], {
                queryParams: {
                    layout: null,
                },
                queryParamsHandling: 'merge',
            })
            .then(() => {
                // Set the config
                this._fuseConfigService.config = { layout };
            });
    }

    isAuthenticatedUser(): boolean {
        return this._authService.accessToken ? true : false;
    }

    constrainFabPosition = (point: Point): Point => {
        const y = Math.max(48, Math.min(window.innerHeight - 48, point.y));
        return { x: point.x, y };
    };

    onFabDragEnded(event: CdkDragEnd): void {
        this._wasDragged = true;
        const pos = event.source.getFreeDragPosition();
        this.fabDragPosition = { x: 0, y: pos.y };
    }

    openCreateTicketModal(): void {
        if (this._wasDragged) {
            this._wasDragged = false;
            return;
        }
        this.dialog.open(HelpDeskComponent, {
            panelClass: ['md:w-3/5', 'w-full'],
            maxHeight: '85vh',
            data: {},
        });
    }
}
